import { IonContent, IonPage } from '@ionic/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';

import './auth.css';
import { auth } from '../firebase';
import {
  RR_LOGO_URL,
  Field,
  TextInput,
  BackIcon,
  MailIcon,
  InfoIcon,
  AlertIcon,
  isValidEmail,
} from './authShared';

const Reset: React.FC = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function sendReset(e?: React.FormEvent) {
    e?.preventDefault();
    if (busy) return;
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSent(true);
    } catch (err: any) {
      // Don't disclose whether an account exists — a missing account still
      // lands on "Check your email". Only surface true failures.
      if (err?.code === 'auth/user-not-found') {
        setSent(true);
      } else if (err?.code === 'auth/invalid-email') {
        setError('That email address looks invalid.');
      } else {
        setError('Something went wrong sending the reset link. Please try again.');
      }
      // eslint-disable-next-line no-console
      console.log(err?.code, err?.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <IonPage>
      <IonContent fullscreen className="rr-auth-content">
        <div className="auth auth--centered">
          <div className="auth-card-wrap">
            <div className="auth-card">
              {!sent ? (
                <>
                  <div className="auth-card-mark">
                    <span className="auth-brand-logo">
                      <img src={RR_LOGO_URL} alt="Rollover Rep" />
                    </span>
                  </div>
                  <header className="auth-heading">
                    <div className="auth-eyebrow">Reset password</div>
                    <h2>Forgot your password?</h2>
                    <p>Enter the email associated with your Rollover Rep account and we’ll send you a link to reset it.</p>
                  </header>

                  {error && (
                    <div className="auth-banner auth-banner--error">
                      <AlertIcon />
                      <span>{error}</span>
                    </div>
                  )}

                  <form className="auth-fields" onSubmit={sendReset}>
                    <Field label="Work email">
                      <TextInput type="email" value={email} onChange={setEmail} placeholder="you@facility.com" autoComplete="email" />
                    </Field>
                    <button type="submit" className="auth-submit" disabled={busy}>
                      {busy ? 'Sending…' : 'Send reset link'}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <div className="auth-success-icon">
                    <MailIcon />
                  </div>
                  <header className="auth-heading">
                    <div className="auth-eyebrow">Check your inbox</div>
                    <h2>We sent you a link.</h2>
                    <p>
                      A password reset link is on its way to{' '}
                      <strong style={{ color: 'var(--fg-1)' }}>{email}</strong>. It expires in 30 minutes.
                    </p>
                  </header>

                  <div className="auth-banner auth-banner--info">
                    <InfoIcon />
                    <span>
                      Didn’t get the email? Check your spam folder, or{' '}
                      <button
                        type="button"
                        onClick={() => sendReset()}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          color: 'inherit',
                          fontWeight: 600,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        resend it
                      </button>.
                    </span>
                  </div>
                </>
              )}

              <Link className="auth-back" to="/Login">
                <BackIcon />
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Reset;
